import { DOCUMENT } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { BarElement, Chart, ChartData, ChartOptions, Plugin, ScriptableContext, TooltipItem } from 'chart.js';
import { color, toFont } from 'chart.js/helpers';
import { Uc2SignalTimelineRow } from '../../models/uc2-view-state';

const WAT_TIME_ZONE = 'Africa/Lagos';

@Component({
  selector: 'app-uc2-onset-timeline',
  imports: [BaseChartDirective],
  templateUrl: './uc2-onset-timeline.component.html',
  styleUrl: './uc2-onset-timeline.component.scss',
})
export class Uc2OnsetTimelineComponent {
  rows = input.required<Uc2SignalTimelineRow[]>();
  selectedMemberId = input<string | null>(null);

  memberSelected = output<string>();

  private _document = inject(DOCUMENT);
  hasEvents = computed(() => this.rows().some(row => row.events?.length));
  chartHeight = computed(() => this.hasEvents() ? `${Math.max(14, this.rows().length * 4)}rem` : null);
  readonly eventPlugins: Plugin<'bar'>[] = [{
    id: 'well-pressure-events',
    afterDatasetsDraw: chart => {
      const { ctx, scales, chartArea } = chart;
      ctx.save();
      this.rows().forEach((row, rowIndex) => row.events?.forEach((event, eventIndex) => {
        const bar = chart.getDatasetMeta(eventIndex).data[rowIndex];
        if (!(bar instanceof BarElement)) return;
        const { y, height } = bar.getProps(['y', 'height'], true);
        if (y === null) return;
        const start = scales['x'].getPixelForValue(event.startMs);
        const finish = scales['x'].getPixelForValue(event.endMs);
        const bracket = scales['x'].getPixelForValue(event.onsetNotBeforeMs);
        ctx.fillStyle = color(this.themeColor('--status-amber')).alpha(0.35).rgbString();
        if (event.settledMs !== null) {
          const settled = scales['x'].getPixelForValue(event.settledMs);
          ctx.clearRect(settled, y - height / 2 + 2, Math.max(0, finish - settled - 2), height - 4);
          ctx.fillRect(settled, y - height / 2 + 2, Math.max(0, finish - settled - 2), height - 4);
          ctx.fillStyle = this.themeColor('--chv-dm-dark-gray');
          ctx.beginPath();
          ctx.moveTo(settled, y - height / 2);
          ctx.lineTo(settled + 4, y - height / 2 + 5);
          ctx.lineTo(settled, y - height / 2 + 10);
          ctx.lineTo(settled - 4, y - height / 2 + 5);
          ctx.fill();
        }
        ctx.strokeStyle = this.themeColor('--chv-dm-gray');
        const hatch = (from: number, to: number) => {
          ctx.save();
          ctx.beginPath();
          ctx.rect(Math.max(chartArea.left, from), y - height / 2, Math.max(0, to - Math.max(chartArea.left, from)), height);
          ctx.clip();
          ctx.globalAlpha = 0.4;
          for (let x = from - height; x < to; x += 6) {
            ctx.beginPath(); ctx.moveTo(x, y + height / 2); ctx.lineTo(x + height, y - height / 2); ctx.stroke();
          }
          ctx.restore();
        };
        hatch(bracket, start);
        event.gaps.forEach(([from, to]) => hatch(scales['x'].getPixelForValue(from), scales['x'].getPixelForValue(to)));
        ctx.fillStyle = this.themeColor('--chv-dm-dark-gray');
        ctx.font = toFont(Chart.defaults.font).string;
        ctx.fillText(this.formatTime(event.startMs), Math.min(start + 4, chartArea.right - 120), y - height / 2 - 4);
        if (event.rightCensored) ctx.fillText('→', finish - 14, y + 4);
      }));
      ctx.restore();
    },
  }];

  chartData = computed<ChartData<'bar', ([number, number] | null)[], string>>(() => {
    this.selectedMemberId();
    const rows = this.rows();
    const intervalCount = Math.max(1, ...rows.map(row => row.intervals?.length ?? 1));

    return {
      labels: rows.map(row => row.asset),
      datasets: Array.from({ length: intervalCount }, (_, intervalIndex) => ({
        label: 'Pressure deviation',
        data: rows.map(row => row.intervals
          ? row.intervals[intervalIndex] ?? null
          : intervalIndex === 0 ? [row.startMs, row.endMs] : null),
        backgroundColor: () => this.themeColor('--status-amber'),
        borderColor: context => this.resolveSignalBorderColor(context),
        borderWidth: context => this.resolveSignalBorderWidth(context),
        borderSkipped: false,
        grouped: false,
      })),
    };
  });

  chartOptions = computed<ChartOptions<'bar'>>(() => {
    const rows = this.rows();

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      indexAxis: 'y',
      scales: {
        x: {
          type: 'linear',
          min: Math.min(...rows.map(row => row.events?.[0]?.onsetNotBeforeMs ?? row.startMs)),
          max: Math.max(...rows.map(row => row.endMs)),
          title: { display: true, text: 'Observation time (WAT)' },
          ticks: {
            autoSkip: true,
            autoSkipPadding: 16,
            maxRotation: 0,
            maxTicksLimit: 5,
            callback: value => this.formatAxisTime(Number(value)),
          },
        },
        y: {
          grid: { display: false },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: items => this.tooltipTitle(items[0]),
            label: item => this.tooltipLines(item),
          },
        },
      },
    };
  });

  onChartClick(active: object[] | undefined): void {
    const first = active?.[0];
    const index = first && 'index' in first && typeof first.index === 'number' ? first.index : -1;
    const datasetIndex = first && 'datasetIndex' in first && typeof first.datasetIndex === 'number'
      ? first.datasetIndex
      : -1;
    const row = this.rows()[index];
    const interval = this.chartData().datasets[datasetIndex]?.data[index];
    if (row && interval) {
      this.memberSelected.emit(row.exceptionId);
    }
  }

  private resolveSignalBorderColor(context: ScriptableContext<'bar'>): string {
    const row = this.rows()[context.dataIndex];
    return this.isSelected(row)
      ? this.themeColor('--chv-dm-dark-gray')
      : this.themeColor('--status-amber');
  }

  private resolveSignalBorderWidth(context: ScriptableContext<'bar'>): number {
    const row = this.rows()[context.dataIndex];
    return this.isSelected(row) ? 4 : 1;
  }

  private isSelected(row: Uc2SignalTimelineRow | undefined): boolean {
    return row?.exceptionId === this.selectedMemberId() || !!row?.memberIds?.includes(this.selectedMemberId() ?? '');
  }

  private tooltipTitle(item: TooltipItem<'bar'> | undefined): string {
    const row = this.rows()[item?.dataIndex ?? -1];
    return row?.signalClass ?? '';
  }

  private tooltipLines(item: TooltipItem<'bar'>): string[] {
    const row = this.rows()[item.dataIndex];
    const interval = this.chartData().datasets[item.datasetIndex]?.data[item.dataIndex];
    if (!row || !interval) {
      return [];
    }

    const event = row.events?.[item.datasetIndex];
    return [
      `Asset: ${row.asset}`,
      `Measurement: ${row.signalClass}`,
      `Start: ${this.formatTime(interval[0])} WAT`,
      ...(event ? [
        `Onset bracket: ${this.formatTime(event.onsetNotBeforeMs)} – ${this.formatTime(event.startMs)} WAT`,
        `Observed through: ${this.formatTime(event.endMs)} WAT`,
        event.settledMs === null ? 'Settling not established' : `Sampled settling: ${this.formatTime(event.settledMs)} WAT (not recovery)`,
        event.coverageUncertain ? 'Continuation uncertain after coverage loss' : event.rightCensored ? 'Return to reference not observed' : 'Return to reference observed',
      ] : []),
      ...row.context.map(entry => `${entry.label}: ${entry.value}`),
    ];
  }

  private formatTime(value: number): string {
    return new Date(value).toLocaleString(undefined, {
      timeZone: WAT_TIME_ZONE,
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private formatAxisTime(value: number): string[] {
    const date = new Date(value);
    return [
      date.toLocaleDateString(undefined, {
        timeZone: WAT_TIME_ZONE,
        month: 'short',
        day: 'numeric',
      }),
      date.toLocaleTimeString(undefined, {
        timeZone: WAT_TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit',
      }),
    ];
  }

  private themeColor(variable: string): string {
    return getComputedStyle(this._document.documentElement).getPropertyValue(variable).trim();
  }
}